import { buildPermissionIndex, indexMatches } from './matcher';

/**
 * The one blessed checking API. `can` takes a single key; `canAny`/`canAll`
 * take lists. No string-or-array overloading.
 *
 * Super admins need no special case here: the backend emits a reserved '*'
 * grant on auth/me which the matcher treats as matching every key.
 *
 * Keys are typically members of the generated Permissions module; plain
 * strings are accepted because server-driven config (summary controls, form
 * fields, data lists, workflow guards) carries permission names as data.
 */
export type CanApi = {
  can: (permission: string) => boolean;
  canAny: (permissions: readonly string[]) => boolean;
  canAll: (permissions: readonly string[]) => boolean;
  /**
   * Declarative config requirements (a single key or a list); a list
   * requires every key. This is how config-driven gating (nav items, tabs,
   * actions, routes, deal types) is evaluated.
   */
  meetsRequirement: (requirement: string | readonly string[]) => boolean;
};

export const buildCanApi = (granted: readonly string[]): CanApi => {
  const index = buildPermissionIndex(granted);

  return {
    can: (permission) => indexMatches(index, permission),
    canAny: (permissions) =>
      permissions.some((permission) => indexMatches(index, permission)),
    canAll: (permissions) =>
      permissions.every((permission) => indexMatches(index, permission)),
    meetsRequirement: (requirement) =>
      (typeof requirement === 'string' ? [requirement] : requirement).every(
        (permission) => indexMatches(index, permission),
      ),
  };
};

/**
 * Anything carrying a grants array: the current user, an organisation or
 * brokerage, or a server payload.
 */
export type Grantee = { permissions?: readonly string[] } | null | undefined;
